// Lib
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Helmet from 'react-helmet';
import { debounce } from 'lodash';

// Components
import ErrorBoundary from '../../../components/error/errorBoundary/ErrorBoundary';

// Selectors
import { getThemeIsDarkMode } from '../../../user/account/accountModal/interface/themeSettings/themeSelector';

const WINDOW_CONTROLS_OVERLAY_CLASS = 'pwa-window-controls-overlay';
const WINDOW_TITLE_BAR_CLASS = 'pwa-window-title-bar';

const mapStateToProps = createStructuredSelector({
    isDarkMode: getThemeIsDarkMode,
});

const EmptyErrorRenderer = () => null;

const getMetaThemeColor = (isUsingWindowOverlay, isDarkMode) => {
    // If not using the overlay, we use a slightly different grey colour so the title bar doesn't blend
    // into the header when in dark mode
    if (!isUsingWindowOverlay) return '#3d3d3d';

    // Otherwise, match the title bar overlay with the app header
    return isDarkMode ? '#333333' : '#ffffff';
};

/**
 * Reacts to switching between dark and light mode, or enabling and disabling the window controls overlay,
 * and sets the PWA title bar theme based on that state.
 */
const PwaTitleBarStyle = (props) => {
    const initialMetaThemeColor = getMetaThemeColor(navigator.windowControlsOverlay?.visible, props.isDarkMode);
    const [metaThemeColor, setMetaThemeColor] = React.useState(initialMetaThemeColor);

    const setBodyClass = React.useCallback((isUsingWindowOverlay) => {
        const classToAdd = isUsingWindowOverlay ? WINDOW_CONTROLS_OVERLAY_CLASS : WINDOW_TITLE_BAR_CLASS;
        const classToRemove = isUsingWindowOverlay ? WINDOW_TITLE_BAR_CLASS : WINDOW_CONTROLS_OVERLAY_CLASS;
        document.body.classList.remove(classToRemove);
        document.body.classList.add(classToAdd);
    }, []);

    React.useEffect(() => {
        if (!navigator.windowControlsOverlay?.addEventListener) return;

        let isUsingWindowOverlay = navigator.windowControlsOverlay.visible;

        setBodyClass(isUsingWindowOverlay);
        setMetaThemeColor(getMetaThemeColor(isUsingWindowOverlay, props.isDarkMode));

        const onGeometryChange = debounce(
            (event) => {
                const nowIsUsingWindowOverlay = navigator.windowControlsOverlay.visible;

                if (isUsingWindowOverlay !== nowIsUsingWindowOverlay) {
                    isUsingWindowOverlay = nowIsUsingWindowOverlay;

                    setBodyClass(isUsingWindowOverlay);
                    setMetaThemeColor(getMetaThemeColor(isUsingWindowOverlay, props.isDarkMode));
                }
            },
            100,
            { leading: true },
        );

        navigator.windowControlsOverlay.addEventListener('geometrychange', onGeometryChange);

        return () => {
            navigator.windowControlsOverlay.removeEventListener('geometrychange', onGeometryChange);
        };
    }, [props.isDarkMode]);

    useEffect(() => {
        const resetTitleColor = () => {
            document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#3d3d3d');
            if (navigator.windowControlsOverlay?.visible)
                document.querySelector('.WorkspaceToolsHeader')?.classList.add('initial-title-bar-color');
        };

        window.addEventListener('beforeunload', resetTitleColor);
    }, []);

    return (
        <ErrorBoundary ErrorRenderer={EmptyErrorRenderer}>
            <Helmet>
                <meta name="theme-color" content={metaThemeColor} />
            </Helmet>
        </ErrorBoundary>
    );
};

PwaTitleBarStyle.propTypes = {
    isDarkMode: PropTypes.bool,
};

export default connect(mapStateToProps)(PwaTitleBarStyle);
